import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listNotifications, markRead, unreadCount, type NotificationItem } from "./api";
import { enablePush, pushPermission } from "./push";

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState<NotificationPermission | "unsupported">(pushPermission());
  const [enabling, setEnabling] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const refreshCount = useCallback(() => {
    unreadCount()
      .then((r) => setCount(r.count))
      .catch(() => {});
  }, []);

  // Poll the unread count on a calm cadence — this is the "we'll notify you"
  // signal. 25s keeps it cheap while feeling live.
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 25_000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const onOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      listNotifications(20)
        .then((rows) => {
          setItems(rows);
          // Opening the panel marks everything read.
          if (rows.some((r) => !r.read)) {
            markRead().then(() => setCount(0)).catch(() => {});
          }
        })
        .catch(() => {});
    }
  };

  const onItem = (n: NotificationItem) => {
    setOpen(false);
    // Render notifications deep-link to the Renders surface, auto-opening the
    // finished (or failed) render. ``render_failed`` lands on the Failed tab;
    // ``render_complete`` on Completed. When the backend attached a render_id
    // we pass it so RendersPage opens that render's detail sheet directly;
    // otherwise we still land the user on the right tab of /renders.
    if (n.kind === "render_failed" || n.kind === "render_complete") {
      const tab = n.kind === "render_failed" ? "failed" : "complete";
      const qs = new URLSearchParams({ tab });
      if (n.render_id) qs.set("render", n.render_id);
      navigate(`/renders?${qs.toString()}`);
      return;
    }
    // Any other notification kind: fall back to the library.
    navigate("/library");
  };

  // Human title fallback — the backend supplies ``title`` for render
  // notifications, but if an older row lacks one we still render something
  // sensible per kind instead of an empty line.
  const titleFor = (n: NotificationItem): string => {
    if (n.title?.trim()) return n.title;
    if (n.kind === "render_complete") return "Your render is ready";
    if (n.kind === "render_failed") return "A render didn't finish";
    return "Notification";
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        aria-label="Notifications"
        onClick={onOpen}
        style={{
          position: "relative",
          width: 32,
          height: 32,
          borderRadius: 9999,
          border: "1px solid var(--border)",
          background: "var(--bg-elev)",
          color: "var(--fg-muted)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && (
          <span
            style={{
              position: "absolute",
              top: -3,
              right: -3,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 9999,
              background: "#e5484d",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: 40,
            right: 0,
            width: 320,
            maxHeight: 420,
            overflowY: "auto",
            background: "var(--bg-elev, #16161a)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
            zIndex: 50,
          }}
        >
          <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 500 }}>
            Notifications
          </div>
          {perm === "default" && (
            <button
              onClick={() => {
                setEnabling(true);
                enablePush()
                  .catch(() => false)
                  .finally(() => {
                    setPerm(pushPermission());
                    setEnabling(false);
                  });
              }}
              disabled={enabling}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                border: "none",
                borderBottom: "1px solid var(--border)",
                background: "transparent",
                color: "var(--fg)",
                cursor: "pointer",
                fontSize: 12.5,
              }}
            >
              🔔 {enabling ? "Enabling…" : "Get notified even when this tab is closed"}
            </button>
          )}
          {items.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--fg-subtle)", fontSize: 13 }}>
              Nothing yet — we'll let you know when a render is ready.
            </div>
          ) : (
            items.map((n) => {
              const failed = n.kind === "render_failed";
              return (
              <button
                key={n.id}
                onClick={() => onItem(n)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  border: "none",
                  borderBottom: "1px solid var(--border)",
                  // Failures read as a warning: a soft red wash + left accent
                  // so a failed render stands out from the (more common) ready
                  // notifications even at a glance.
                  background: failed
                    ? "rgba(229,72,77,0.08)"
                    : n.read
                      ? "transparent"
                      : "var(--bg-hover, rgba(255,255,255,0.03))",
                  borderLeft: failed ? "2px solid #e5484d" : "2px solid transparent",
                  color: "var(--fg)",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    aria-hidden
                    style={{
                      fontSize: 14,
                    }}
                  >
                    {failed ? "⚠️" : "✅"}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      flex: 1,
                      color: failed ? "#f2a0a2" : "var(--fg)",
                    }}
                  >
                    {titleFor(n)}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--fg-subtle)" }}>{timeAgo(n.created_at)}</span>
                </div>
                {n.body && (
                  <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4, marginLeft: 22 }}>
                    {n.body}
                  </div>
                )}
              </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
